import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { copyToClipboard } from "../lib/clipboard";
import { useAuth } from "../AuthContext";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import { CodeCardDialog } from "../components/CodeCardDialog";
import GodModeTransferDialog from "../components/GodModeTransferDialog";
import AnnouncementDialog from "../components/AnnouncementDialog";
import { Plus, Users, Copy, Link as LinkIcon, Trash, ArrowRight, Warning, ArrowFatUp, QrCode, ArrowsLeftRight, CaretDown, MapPin, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

// v414.9 — region grouping. Order mirrors CreateDistributorDialog REGIONS;
// distributors without a saved region fall back to their code prefix.
const REGION_ORDER = [
  "Alberta", "British Columbia", "Ontario", "Quebec", "Manitoba",
  "Saskatchewan", "Nova Scotia", "New Brunswick", "Newfoundland",
  "Prince Edward Island", "Yukon", "Northwest Territories", "Nunavut",
  "California (US)", "USA — Other", "Mexico",
];
const PREFIX_TO_REGION = {
  AB: "Alberta", BC: "British Columbia", ON: "Ontario", QC: "Quebec",
  MB: "Manitoba", SK: "Saskatchewan", NS: "Nova Scotia", NB: "New Brunswick",
  NL: "Newfoundland", PE: "Prince Edward Island", YT: "Yukon",
  NT: "Northwest Territories", NU: "Nunavut",
  CA: "California (US)", US: "USA — Other", MX: "Mexico",
};
const regionOf = (d) =>
  d.region || PREFIX_TO_REGION[(d.distributor_code || "").slice(0, 2).toUpperCase()] || null;

export default function DistributorsPage() {
  const { isElevated, isOwner } = useAuth();
  const { t } = useTranslation();
  const [distributors, setDistributors] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const [codeCardFor, setCodeCardFor] = useState(null);
  // v414.9 — which region sections are expanded (persisted across visits)
  const [openRegions, setOpenRegions] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("dist_regions_open") || "[]")); }
    catch { return new Set(); }
  });
  const toggleRegion = (name) => {
    setOpenRegions((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      localStorage.setItem("dist_regions_open", JSON.stringify([...next]));
      return next;
    });
  };
  const setRegion = async (d, region) => {
    try {
      await api.patch(`/distributors/${d.id}/region`, { region: region || null });
      toast.success(t("distributors.region_saved", { name: d.name, region: region || t("distributors.region_none") }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [resendingId, setResendingId] = useState(null);
  const [resendCreds, setResendCreds] = useState(null);
  const resendWelcome = async (d) => {
    if (!window.confirm(t("distributors.resend_welcome_confirm", { name: d.name }))) return;
    setResendingId(d.id);
    try {
      const { data } = await api.post(`/distributors/${d.id}/resend-welcome`);
      toast.success(t("distributors.resend_welcome_sent_reset", { email: data.sent_to }));
      if (data.temp_password) {
        setResendCreds({ name: d.name, email: data.sent_to, password: data.temp_password });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResendingId(null);
    }
  };

  const load = async () => {
    try {
      const [d, i] = await Promise.all([
        api.get("/distributors"),
        api.get("/invites"),
      ]);
      // v400.98: personal-use holders stay functional (give/assign pickers,
      // settlements, balance card on the partner page) but are hidden here —
      // owner: "they clutter the dashboard, no sense as separate entities".
      setDistributors(d.data.filter((x) => !x.is_personal));
      setInvites(i.data.filter((x) => !x.used));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeDist = async (id) => {
    if (!window.confirm(t("distributors.delete_confirm"))) return;
    try {
      await api.delete(`/distributors/${id}`);
      toast.success(t("distributors.removed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeInvite = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="distributors-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {isElevated ? t("distributors.title_admin") : t("distributors.title_distributor")}
          </h1>
          <p className="text-sm sm:text-base text-zinc-600 mt-1">
            {isElevated ? t("distributors.subtitle_admin") : t("distributors.subtitle_distributor")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {isOwner && (
            <BrutalButton variant="secondary" onClick={() => setShowTransfer(true)} data-testid="open-god-transfer-btn">
              <ArrowsLeftRight size={16} weight="bold" /> {t("godtransfer.btn")}
            </BrutalButton>
          )}
          {isOwner && (
            <BrutalButton variant="secondary" onClick={() => setShowAnnounce(true)} data-testid="open-announce-btn">
              <EnvelopeSimple size={16} weight="bold" /> {t("announce.open_btn")}
            </BrutalButton>
          )}
          <BrutalButton variant="secondary" onClick={() => setShowQuick(true)} data-testid="open-quick-btn">
            <Plus size={16} weight="bold" /> {t("distributors.quick_btn")}
          </BrutalButton>
          <BrutalButton variant="secondary" onClick={() => setShowInvite(true)} data-testid="open-invite-btn">
            <LinkIcon size={16} weight="bold" /> {t("distributors.invite_link")}
          </BrutalButton>
          <BrutalButton onClick={() => setShowCreate(true)} data-testid="open-create-btn">
            <Plus size={16} weight="bold" /> {isElevated ? t("distributors.new_distributor") : t("distributors.new_sub_distributor")}
          </BrutalButton>
        </div>
      </div>

      <section>
        <h2 className="text-base sm:text-lg font-semibold mb-3 text-zinc-950">{t("distributors.active")}</h2>
        {loading ? (
          <p className="text-sm text-zinc-500">{t("common.loading")}</p>
        ) : distributors.length === 0 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-600">
            {t("distributors.none_yet")}
          </div>
        ) : (
          (() => {
            // v414.9 — group by region into collapsible province sections
            const map = new Map();
            distributors.forEach((d) => {
              const key = regionOf(d) || "__none";
              if (!map.has(key)) map.set(key, []);
              map.get(key).push(d);
            });
            const groups = [...map.entries()].sort((a, b) => {
              if (a[0] === "__none") return 1;
              if (b[0] === "__none") return -1;
              const ia = REGION_ORDER.indexOf(a[0]);
              const ib = REGION_ORDER.indexOf(b[0]);
              return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a[0].localeCompare(b[0]);
            });
            return (
              <div className="space-y-3">
                {groups.map(([regionKey, items]) => {
                  const label = regionKey === "__none" ? t("distributors.region_none") : regionKey;
                  const isOpen = openRegions.has(regionKey) || groups.length === 1;
                  const lowSum = items.reduce((s, d) => s + (d.low_stock_count || 0), 0);
                  return (
                    <div key={regionKey} className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid={`region-group-${regionKey}`}>
                      <button
                        type="button"
                        onClick={() => toggleRegion(regionKey)}
                        className="w-full flex items-center gap-2.5 px-4 py-3.5 text-left hover:bg-zinc-50 transition-colors"
                        data-testid={`region-toggle-${regionKey}`}
                      >
                        <MapPin size={16} weight="bold" className="text-[#8a6a1c] shrink-0" />
                        <span className="font-semibold text-zinc-900 text-sm">{label}</span>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#fbf6e7] border border-[#c9a24a] text-[#8a6a1c] tabular-nums" data-testid={`region-count-${regionKey}`}>
                          {items.length}
                        </span>
                        {lowSum > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700">
                            <Warning size={12} weight="fill" /> {lowSum} {t("distributors.low")}
                          </span>
                        )}
                        <span className="flex-1" />
                        <CaretDown size={14} weight="bold" className={`text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 pt-1">
                          {items.map((d) => (
              <div
                key={d.id}
                data-testid={`distributor-card-${d.id}`}
                className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-zinc-300 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded-lg flex items-center justify-center font-semibold text-sm shrink-0">
                    {d.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-zinc-950 truncate">{d.name}</h3>
                      {d.distributor_code && (
                        <span
                          className="text-[11px] font-bold px-1.5 py-0.5 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded font-mono tracking-wider whitespace-nowrap"
                          data-testid={`dist-code-${d.id}`}
                        >
                          {d.distributor_code}
                        </span>
                      )}
                      {d.is_quick && (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full whitespace-nowrap"
                          data-testid={`guest-badge-${d.id}`}
                        >
                          {t("distributors.guest_badge")}
                        </span>
                      )}
                      {d.role && d.role !== "distributor" && (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded-full whitespace-nowrap"
                          data-testid={`partner-badge-${d.id}`}
                        >
                          {t("distributors.partner_badge")}
                        </span>
                      )}
                      {(d.depth ?? 1) > 1 && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-full whitespace-nowrap">
                          {t("distributors.sub_level", { depth: d.depth })}
                        </span>
                      )}
                    </div>
                    {!d.is_quick && (
                      <p className="text-xs text-zinc-500 truncate">{d.email}</p>
                    )}
                    {d.is_quick && d.phone && (
                      <p className="text-xs text-zinc-500 truncate">{d.phone}</p>
                    )}
                    {d.parent_name && isElevated && (d.depth ?? 1) > 1 && (
                      <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{t("distributors.via", { name: d.parent_name })}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-zinc-600">{d.product_count} {t("distributors.products")}</span>
                  {d.low_stock_count > 0 ? (
                    <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                      <Warning size={14} weight="fill" /> {d.low_stock_count} {t("distributors.low")}
                    </span>
                  ) : (
                    <span className="text-zinc-400">{t("common.all_good")}</span>
                  )}
                </div>
                {isElevated && d.role === "distributor" && (
                  <PaymentStatusToggle
                    distributor={d}
                    onChanged={load}
                  />
                )}
                {isElevated && d.is_quick && (
                  <div className="mb-3">
                    <BrutalButton
                      size="sm"
                      variant="secondary"
                      className="w-full !border-emerald-400 !text-emerald-800"
                      onClick={() => setPromoting(d)}
                      data-testid={`promote-btn-${d.id}`}
                    >
                      <ArrowFatUp size={14} weight="bold" /> {t("promote.button")}
                    </BrutalButton>
                  </div>
                )}
                {isElevated && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <MapPin size={13} weight="bold" className="text-zinc-400 shrink-0" />
                    <select
                      value={d.region || ""}
                      onChange={(e) => setRegion(d, e.target.value)}
                      className="flex-1 h-8 px-2 rounded-lg border border-zinc-200 bg-zinc-50 text-[12px] font-medium text-zinc-700 focus:outline-none focus:border-[#C9A84C] cursor-pointer"
                      data-testid={`dist-region-select-${d.id}`}
                    >
                      <option value="">{d.region ? t("distributors.region_none") : t("distributors.region_unset", { region: regionOf(d) || t("distributors.region_none") })}</option>
                      {REGION_ORDER.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex gap-2">
                  <Link to={`/distributors/${d.id}`} className="flex-1">
                    <BrutalButton size="sm" variant="secondary" className="w-full" data-testid={`view-distributor-${d.id}`}>
                      {t("distributors.view")} <ArrowRight size={14} weight="bold" />
                    </BrutalButton>
                  </Link>
                  {d.distributor_code && (
                    <BrutalButton
                      size="sm"
                      variant="secondary"
                      className="!border-[#c9a24a] !text-[#8a6a1c]"
                      onClick={() => setCodeCardFor(d)}
                      data-testid={`code-card-btn-${d.id}`}
                      title={t("code_card.title")}
                    >
                      <QrCode size={14} weight="bold" />
                    </BrutalButton>
                  )}
                  {isElevated && !d.is_quick && d.role === "distributor" && (
                    <BrutalButton
                      size="sm"
                      variant="secondary"
                      disabled={resendingId === d.id}
                      onClick={() => resendWelcome(d)}
                      data-testid={`resend-welcome-btn-${d.id}`}
                      title={t("distributors.resend_welcome")}
                    >
                      <EnvelopeSimple size={14} weight="bold" />
                    </BrutalButton>
                  )}
                  {d.role === "distributor" && (
                    <BrutalButton size="sm" variant="warning" onClick={() => removeDist(d.id)} data-testid={`delete-distributor-${d.id}`}>
                      <Trash size={14} weight="bold" />
                    </BrutalButton>
                  )}
                </div>
              </div>
            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}
      </section>

      {invites.length > 0 && (
        <section>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">{t("distributors.pending_invites")}</h2>
          <div className="space-y-3">
            {invites.map((inv) => (
              <InviteRow key={inv.id} invite={inv} onDelete={() => removeInvite(inv.id)} />
            ))}
          </div>
        </section>
      )}

      {showCreate && (
        <CreateDistributorDialog onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {showInvite && (
        <CreateInviteDialog onClose={() => setShowInvite(false)} onCreated={load} />
      )}
      {showQuick && (
        <QuickConsigneeDialog onClose={() => setShowQuick(false)} onCreated={load} />
      )}
      {showAnnounce && <AnnouncementDialog onClose={() => setShowAnnounce(false)} />}
      {showTransfer && (
        <GodModeTransferDialog onClose={() => setShowTransfer(false)} onDone={load} />
      )}
      {promoting && (
        <PromoteDialog guest={promoting} onClose={() => setPromoting(null)} onDone={load} />
      )}
      {codeCardFor && (
        <CodeCardDialog distributor={codeCardFor} onClose={() => setCodeCardFor(null)} />
      )}
      {resendCreds && (
        <TempPasswordDialog creds={resendCreds} onClose={() => setResendCreds(null)} />
      )}
    </div>
  );
}

// v414.22 — shown ONCE after "Resend welcome email": the fresh temp password is
// only visible here (DB stores a one-way hash), so the admin can copy/hand it off.
function TempPasswordDialog({ creds, onClose }) {
  const { t } = useTranslation();
  return (
    <Modal onClose={onClose} testId="temp-password-dialog">
      <h2 className="text-lg font-semibold mb-1">{t("distributors.temp_pw_title")}</h2>
      <p className="text-sm text-zinc-600 mb-3">
        {t("distributors.temp_pw_subtitle", { name: creds.name || creds.email })}
      </p>
      <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 font-mono text-sm space-y-1" data-testid="temp-password-value">
        <div><strong>{t("common.email")}:</strong> {creds.email}</div>
        <div><strong>{t("common.password")}:</strong> {creds.password}</div>
      </div>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
        {t("distributors.temp_pw_once_note")}
      </p>
      <BrutalButton
        className="w-full mt-3"
        onClick={() => copyToClipboard(
          `Email: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`,
          t("common.copied")
        )}
        data-testid="temp-password-copy"
      >
        <Copy size={14} weight="bold" /> {t("distributors.copy_creds")}
      </BrutalButton>
      <BrutalButton variant="secondary" className="w-full mt-2" onClick={onClose} data-testid="temp-password-done">
        {t("common.done")}
      </BrutalButton>
    </Modal>
  );
}

function PromoteDialog({ guest, onClose, onDone }) {
  const { t } = useTranslation();
  const [parents, setParents] = useState([]);
  const [parentId, setParentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/distributors/promote-parents");
        const eligible = data.filter((p) => p.id !== guest.id);
        setParents(eligible);
        if (eligible.length > 0) setParentId(eligible[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [guest.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!parentId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/distributors/${guest.id}/promote`, { parent_id: parentId });
      toast.success(t("promote.done", { name: guest.name, code: data.distributor_code || "" }));
      onDone();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border-2 border-emerald-400 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="promote-dialog"
      >
        <h2 className="text-xl font-semibold mb-1">{t("promote.dialog_title")}</h2>
        <p className="text-sm text-zinc-600 mb-4">{t("promote.dialog_subtitle")}</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("common.name")}</label>
            <BrutalInput value={guest.name} readOnly disabled data-testid="promote-name-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("promote.parent_label")}</label>
            {loading ? (
              <p className="text-sm text-zinc-500">{t("common.loading")}</p>
            ) : (
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                data-testid="promote-parent-select"
                className="w-full h-11 px-3 bg-white border border-zinc-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-400"
                required
              >
                {parents.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.role === "partner" ? `· ${t("distributors.partner_badge")}` : ""}{p.distributor_code ? ` (${p.distributor_code})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy || !parentId} data-testid="promote-submit">
              {busy ? t("promote.processing") : t("promote.confirm")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaymentStatusToggle({ distributor, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const current = distributor.payment_status || "consignment";
  const flip = async (next) => {
    if (next === current || busy) return;
    setBusy(true);
    try {
      await api.patch(`/distributors/${distributor.id}/payment-status`, { status: next });
      toast.success(
        next === "paid"
          ? t("distributors.marked_paid", { name: distributor.name })
          : t("distributors.marked_consignment", { name: distributor.name })
      );
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="mb-3 inline-flex w-full rounded-lg border-2 border-[#c9a24a] bg-white p-0.5 text-xs font-semibold overflow-hidden"
      data-testid={`payment-status-toggle-${distributor.id}`}
    >
      <button
        type="button"
        disabled={busy}
        onClick={() => flip("consignment")}
        data-testid={`payment-status-consignment-${distributor.id}`}
        className={`flex-1 h-7 rounded-md transition-colors ${
          current === "consignment"
            ? "bg-[#c9a24a] text-white shadow-sm"
            : "text-[#8a6a1c] hover:bg-[#fbf6e7]"
        }`}
      >
        {t("distributors.status_consignment")}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => flip("paid")}
        data-testid={`payment-status-paid-${distributor.id}`}
        className={`flex-1 h-7 rounded-md transition-colors ${
          current === "paid"
            ? "bg-[#c9a24a] text-white shadow-sm"
            : "text-[#8a6a1c] hover:bg-[#fbf6e7]"
        }`}
      >
        {t("distributors.status_paid")}
      </button>
    </div>
  );
}

function QuickConsigneeDialog({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t("distributors.quick_name_required"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/distributors/quick", {
        name: trimmed,
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t("distributors.quick_created", { name: trimmed }));
      onCreated();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-consignee-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight">{t("distributors.quick_title")}</h2>
        <p className="text-sm text-zinc-600 mt-1 mb-4">{t("distributors.quick_subtitle")}</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.quick_name_label")}</label>
            <BrutalInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("distributors.quick_name_placeholder")}
              maxLength={200}
              data-testid="quick-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.quick_phone_label")}</label>
            <BrutalInput
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 555-0100"
              maxLength={50}
              data-testid="quick-phone-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.quick_notes_label")}</label>
            <textarea
              className="input-brutal w-full h-20 resize-none"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("distributors.quick_notes_placeholder")}
              maxLength={1000}
              data-testid="quick-notes-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="quick-submit">
              {busy ? t("assign.sending") : t("distributors.quick_create_btn")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteRow({ invite, onDelete }) {
  const { t } = useTranslation();
  const copy = () => {
    copyToClipboard(invite.invite_link, t("distributors.link_copied"));
  };
  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`invite-row-${invite.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{invite.email || invite.name || t("distributors.open_invite")}</div>
        <div className="text-xs font-mono text-zinc-500 truncate">{invite.invite_link}</div>
      </div>
      <div className="flex gap-2">
        <BrutalButton size="sm" variant="secondary" onClick={copy} data-testid={`copy-invite-${invite.id}`}>
          <Copy size={14} weight="bold" /> {t("common.copy")}
        </BrutalButton>
        <BrutalButton size="sm" variant="warning" onClick={onDelete}>
          <Trash size={14} weight="bold" />
        </BrutalButton>
      </div>
    </div>
  );
}

function CreateDistributorDialog({ onClose, onCreated }) {
  const { t } = useTranslation();
  // Mirrors backend PROVINCE_PREFIXES (core/helpers.py) — value is the region
  // name the API expects; the label shows the resulting code prefix.
  const REGIONS = [
    ["Alberta", "AB"], ["Ontario", "ON"], ["Quebec", "QC"],
    ["British Columbia", "BC"], ["Manitoba", "MB"], ["Saskatchewan", "SK"],
    ["Nova Scotia", "NS"], ["New Brunswick", "NB"], ["Newfoundland", "NL"],
    ["Prince Edward Island", "PE"], ["Yukon", "YT"],
    ["Northwest Territories", "NT"], ["Nunavut", "NU"],
    // v413.2 — international
    ["California (US)", "CA"], ["USA — Other", "US"], ["Mexico", "MX"],
  ];
  const [form, setForm] = useState({ name: "", email: "", password: "", region: "Alberta", markup: "", markupType: "flat", wellnessMarkup: "", wellnessMarkupType: "flat" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const markup = form.markup.trim() === "" ? null : Number(form.markup);
    if (markup !== null && (!Number.isFinite(markup) || markup < 0)) {
      toast.error(t("distributors.invalid_markup"));
      return;
    }
    const wellnessMarkup = form.wellnessMarkup.trim() === "" ? null : Number(form.wellnessMarkup);
    if (wellnessMarkup !== null && (!Number.isFinite(wellnessMarkup) || wellnessMarkup < 0)) {
      toast.error(t("distributors.invalid_markup"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/distributors", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        region: form.region,
        margin_surcharge: markup,
        margin_type: markup !== null ? form.markupType : null,
        wellness_margin_surcharge: wellnessMarkup,
        wellness_margin_type: wellnessMarkup !== null ? form.wellnessMarkupType : null,
      });
      setCreated({ ...data, password: form.password });
      toast.success(t("distributors.created"));
      // v405.0 onboarding email confirmation
      const ob = data.onboarding_email;
      if (ob?.sent) {
        toast.success(t("distributors.onboarding_sent", { email: form.email.trim().toLowerCase() }));
      } else {
        toast.warning(t("distributors.onboarding_failed", { err: ob?.error || "unknown" }));
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} testId="create-distributor-dialog">
      <h2 className="text-2xl font-semibold mb-4">{t("distributors.new_dialog_title")}</h2>
      {created ? (
        <div className="space-y-4">
          <p className="text-sm">{t("distributors.share_creds")}</p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 space-y-2 font-mono text-sm">
            <div><strong>{t("common.email")}:</strong> {created.email}</div>
            <div><strong>{t("common.password")}:</strong> {created.password}</div>
            {created.distributor_code && (
              <div data-testid="created-distributor-code">
                <strong>{t("distributors.code_label")}:</strong> {created.distributor_code}
              </div>
            )}
          </div>
          <BrutalButton
            className="w-full"
            onClick={() => {
              copyToClipboard(
                `Email: ${created.email}\nPassword: ${created.password}\nLogin: ${window.location.origin}/login`,
                t("distributors.credentials_copied")
              );
            }}
          >
            <Copy size={18} weight="bold" /> {t("distributors.copy_creds")}
          </BrutalButton>
          <BrutalButton variant="secondary" className="w-full" onClick={onClose}>{t("common.done")}</BrutalButton>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("common.name")}</label>
            <BrutalInput
              data-testid="create-distributor-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("common.email")}</label>
            <BrutalInput
              data-testid="create-distributor-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("common.password")}</label>
            <BrutalInput
              data-testid="create-distributor-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              placeholder={t("distributors.min_password")}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.region_label")}</label>
            <select
              className="input-brutal w-full"
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              data-testid="create-distributor-region"
            >
              {REGIONS.map(([name, prefix]) => (
                <option key={prefix} value={name}>{name} ({prefix})</option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-400 mt-0.5">{t("distributors.region_hint")}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.markup_label")}</label>
            <div className="flex gap-2">
              <div className="flex rounded-lg border border-zinc-300 overflow-hidden shrink-0">
                {["flat", "percent"].map((mt) => (
                  <button
                    key={mt}
                    type="button"
                    onClick={() => setForm({ ...form, markupType: mt })}
                    data-testid={`create-distributor-markup-type-${mt}`}
                    className={`px-3 text-sm font-bold transition-colors ${form.markupType === mt ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:text-zinc-800"}`}
                  >
                    {mt === "flat" ? "$" : "%"}
                  </button>
                ))}
              </div>
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{form.markupType === "percent" ? "%" : "$"}</span>
                <BrutalInput
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-6"
                  value={form.markup}
                  onChange={(e) => setForm({ ...form, markup: e.target.value })}
                  placeholder={form.markupType === "percent" ? "10" : "0.00"}
                  data-testid="create-distributor-markup"
                />
              </div>
            </div>
            <p className="text-[10px] text-zinc-400 mt-0.5">{t("distributors.markup_hint")}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.wellness_markup_label")}</label>
            <div className="flex gap-2">
              <div className="flex rounded-lg border border-zinc-300 overflow-hidden shrink-0">
                {["flat", "percent"].map((mt) => (
                  <button
                    key={mt}
                    type="button"
                    onClick={() => setForm({ ...form, wellnessMarkupType: mt })}
                    data-testid={`create-distributor-wellness-markup-type-${mt}`}
                    className={`px-3 text-sm font-bold transition-colors ${form.wellnessMarkupType === mt ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:text-zinc-800"}`}
                  >
                    {mt === "flat" ? "$" : "%"}
                  </button>
                ))}
              </div>
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{form.wellnessMarkupType === "percent" ? "%" : "$"}</span>
                <BrutalInput
                  type="number"
                  min="0"
                  step="0.01"
                  className="pl-6"
                  value={form.wellnessMarkup}
                  onChange={(e) => setForm({ ...form, wellnessMarkup: e.target.value })}
                  placeholder={form.wellnessMarkupType === "percent" ? "10" : "0.00"}
                  data-testid="create-distributor-wellness-markup"
                />
              </div>
            </div>
            <p className="text-[10px] text-zinc-400 mt-0.5">{t("distributors.wellness_markup_hint")}</p>
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="create-distributor-submit">
              {busy ? t("auth.creating") : t("common.create")}
            </BrutalButton>
          </div>
        </form>
      )}
    </Modal>
  );
}

function CreateInviteDialog({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invites", {
        name: form.name.trim() || null,
        email: form.email.trim().toLowerCase() || null,
      });
      setCreated(data);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} testId="create-invite-dialog">
      <h2 className="text-2xl font-semibold mb-4">{t("distributors.invite_dialog_title")}</h2>
      {created ? (
        <div className="space-y-4">
          <p className="text-sm">{t("distributors.invite_dialog_body")}</p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 break-all text-xs font-mono">
            {created.invite_link}
          </div>
          <BrutalButton
            className="w-full"
            onClick={() => {
              copyToClipboard(created.invite_link, t("distributors.link_copied"));
            }}
            data-testid="copy-invite-link"
          >
            <Copy size={18} weight="bold" /> {t("distributors.copy_invite_link")}
          </BrutalButton>
          <BrutalButton variant="secondary" className="w-full" onClick={onClose}>{t("common.done")}</BrutalButton>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-zinc-600">{t("distributors.invite_dialog_form")}</p>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.invite_name_optional")}</label>
            <BrutalInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("distributors.invite_email_optional")}</label>
            <BrutalInput
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="create-invite-submit">
              {busy ? t("distributors.generating") : t("distributors.generate_link")}
            </BrutalButton>
          </div>
        </form>
      )}
    </Modal>
  );
}

function Modal({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
}
